"use client";

import { Menu, ChevronLeft } from "lucide-react"; // X is no longer needed here as the default close button handles it
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
// Ensure SheetTitle and SheetClose are imported
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";

import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

import { Banner } from "./banner/banner";
import { type Section } from "./nav.config";

type ActiveSectionKey = string | null;

export function NavbarMobile({ items }: { items: any }) {
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [activeSectionKey, setActiveSectionKey] =
    useState<ActiveSectionKey>(null);

  useEffect(() => {
    if (isSheetOpen) {
      document.body.classList.add("blur-content");
    } else {
      document.body.classList.remove("blur-content");
      setActiveSectionKey(null);
    }
    return () => {
      document.body.classList.remove("blur-content");
    };
  }, [isSheetOpen]);

  const handleLinkClick = () => {
    setSheetOpen(false);
    setActiveSectionKey(null);
  };

  const currentSection = activeSectionKey
    ? (items[activeSectionKey] as Section)
    : null;
  const mainKeys = Object.keys(items).filter((key) => key !== "contactLabel");
  const isMobile = false;
  return (
    <div className="flex w-full items-center justify-between xl:hidden">
      {/* The original logo and hamburger trigger on the main page */}
      {/* Logo on the left (on the main page) */}
      <div className="flex flex-1 shrink-0 justify-start">
        <Link href="/">
          {" "}
          {/* This link is for the logo visible on the page when drawer is closed */}
          <Image
            src="/webp/logo-lasercamp-512px.webp"
            alt="LaserCamp logo"
            width={160}
            height={45}
            priority
          />
        </Link>
      </div>

      {/* Hamburger on the right (on the main page) */}
      <div className="flex flex-1 justify-end">
        <Sheet open={isSheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Otwórz menu</span>
            </Button>
          </SheetTrigger>

          <SheetContent
            side="top"
            // Re-added pt-12 to push content down below where the header will be.
            // Adjusted padding to create space for the top bar with logo and X.
            // The default Radix X button is often absolutely positioned, so
            // this padding helps prevent overlap.
            className="h-auto max-h-[90vh] w-full overflow-y-auto border-b-0 p-4 pb-8 pt-20"
          >
            {/* SheetTitle is required for accessibility, visually hidden */}
            <SheetTitle className="sr-only">Menu nawigacyjne</SheetTitle>

            {/*
              We need a custom header div for the logo. The Radix SheetClose
              button usually has absolute positioning. We'll rely on its
              default behavior but ensure our custom logo is positioned
              correctly.
            */}
            <div className="absolute left-0 top-0 flex items-center justify-between p-4">
              {/* Logo inside the drawer */}
              <Link href="/" onClick={handleLinkClick}>
                <Image
                  src="/webp/logo-lasercamp-512px.webp"
                  alt="LaserCamp logo"
                  width={160}
                  height={48}
                />
              </Link>
              {/* The default Radix SheetClose button will render here or near here.
                  We don't explicitly add it with <SheetClose asChild><Button><X/></Button></SheetClose>
                  because it's usually part of the SheetContent's internal structure.
                  If it's missing, ensure your ui/sheet.tsx properly includes or exports it.
                  It's typically designed to appear in the top-right.
              */}
              {/* If your default SheetClose button is not rendering, you might need to re-add it
                  inside SheetContent, but then ensure it's not a duplicate.
                  A common pattern for custom close buttons:
                  <SheetClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </SheetClose>
                  However, often you just remove the default X if you want a custom one.
                  Given your problem, the default one is there, so we remove our explicit one.
              */}
            </div>

            {/* Submenu Header and Back Button (below the main header) */}
            {activeSectionKey && currentSection && (
              <div className="mb-4 mt-2 flex items-center border-b border-border pb-4">
                {" "}
                {/* Added mt-2 for spacing */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setActiveSectionKey(null)}
                  className="mr-2"
                >
                  <ChevronLeft className="h-6 w-6" />
                  <span className="sr-only">Wróć do menu głównego</span>
                </Button>
                <h2 className="text-p1 font-semibold">
                  {currentSection.trigger}
                </h2>
              </div>
            )}

            {/* Main Menu (Buttons for categories) */}
            {!activeSectionKey && (
              <ul className="flex flex-col gap-2">
                {mainKeys.map((key) => {
                  const navSection = items[key] as Section;
                  if (!navSection.trigger) return null;

                  return (
                    <li key={key}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start py-5 pl-2 text-p2 font-semibold"
                        onClick={() => setActiveSectionKey(key)}
                      >
                        {navSection.trigger}
                      </Button>
                    </li>
                  );
                })}
                <li>
                  <Link href="/kontakt" onClick={handleLinkClick}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start py-5 pl-2 text-p2 font-semibold"
                    >
                      {items.contactLabel}
                    </Button>
                  </Link>
                </li>
              </ul>
            )}

            {/* Submenu Content */}
            {activeSectionKey && currentSection && (
              <ul className="flex flex-col gap-2 py-1">
                {currentSection.links.map((link) => (
                  <li key={link.title}>
                    <Link
                      href={link.href}
                      className="block rounded-md p-3 transition-colors hover:bg-accent"
                      onClick={handleLinkClick}
                    >
                      <span className="text-base font-medium">
                        {link.title}
                      </span>
                      {"description" in link && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {link.description}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
                {currentSection.banner && (
                  <li className="mt-4 p-1">
                    <Banner {...currentSection.banner} />
                  </li>
                )}
              </ul>
            )}

            <div className="flex justify-end">
              <LanguageSwitcher isMobile />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
